export interface HallSummary {
  hall_id: string;
  display_name: string;
  slug: string;
  default_language: "en" | "es";
  time_zone: string;
  operational_day_close_local: string;
  status: "provisioning" | "active" | "suspended" | "decommissioned" | "archived";
  created_at: string;
  updated_at: string;
}

export class ManagementApiError extends Error {
  public constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ManagementApiError";
  }
}

export class ManagementApi {
  private pendingMutation?: { fingerprint: string; key: string };
  public constructor(
    private readonly baseUrl = "/api",
    private readonly tokenProvider: () => Promise<string> = accessToken,
  ) {}

  public async halls(signal?: AbortSignal): Promise<HallSummary[]> {
    return this.request<HallSummary[]>("/v1/halls", signal);
  }

  public get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, signal);
  }

  public async downloadInstaller(hallId: string, jobId: string): Promise<Blob> {
    if (!/^[0-9a-f-]{36}$/i.test(jobId)) throw new Error("Invalid installer identity");
    const token = await this.tokenProvider();
    const response = await fetch(`${this.baseUrl}/v1/halls/${encodeURIComponent(hallId)}/installers/${jobId}/download`, {
      credentials: "omit", cache: "no-store", redirect: "error",
      headers: { Authorization: `Bearer ${token}`, Accept: "application/octet-stream" },
    });
    if (!response.ok) throw new ManagementApiError(response.status, "Installer download was not authorized or is not ready.");
    return response.blob();
  }

  public async mutate<T>(path: string, method: "POST" | "PUT", payload?: unknown, idempotencyKey?: string): Promise<T> {
    const body = payload === undefined ? undefined : JSON.stringify(payload);
    if (idempotencyKey !== undefined) {
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idempotencyKey)) throw new Error("Invalid mutation identity");
      return this.request<T>(path, undefined, { method, body, key: idempotencyKey });
    }
    const fingerprint = JSON.stringify([path, method, body]);
    if (this.pendingMutation?.fingerprint !== fingerprint) {
      this.pendingMutation = { fingerprint, key: crypto.randomUUID() };
    }
    const key = this.pendingMutation.key;
    const result = await this.request<T>(path, undefined, { method, body, key });
    if (this.pendingMutation?.key === key) this.pendingMutation = undefined;
    return result;
  }

  private async request<T>(path: string, signal?: AbortSignal, mutation?: { method: string; body?: string; key: string }): Promise<T> {
    if (!path.startsWith("/v1/") || path.includes("..")) throw new Error("Invalid management route");
    const token = await this.tokenProvider();
    const response = await fetch(`${this.baseUrl}${path}`, {
      credentials: "omit",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}`,
        ...(mutation ? { "Content-Type": "application/json", "Idempotency-Key": mutation.key } : {}) },
      signal,
      ...(mutation ? { method: mutation.method, body: mutation.body } : {}),
    });
    if (!response.ok) {
      const failure = await response.json().catch(() => ({})) as { detail?: unknown };
      throw new ManagementApiError(response.status, typeof failure.detail === "string" ? failure.detail : `Management API returned ${response.status}`);
    }
    return (await response.json()) as T;
  }
}
import { accessToken } from "./auth";
